import { motion } from "framer-motion";
import { Clock, Gift, Handshake, Shield } from "lucide-react";

const steps = [
  {
    number: 1,
    title: "Search",
    description: "Tell us what you need and your location. Our system instantly matches you with relevant professionals in your area.",
    icon: "🔍"
  },
  {
    number: 2,
    title: "Compare",
    description: "Browse profiles, compare credentials, rates, and client reviews. All professionals are verified for licensing and experience.",
    icon: "⚖️"
  },
  {
    number: 3,
    title: "Connect",
    description: "Contact your chosen professionals directly through our secure platform. Begin your home buying journey with confidence.",
    icon: "🤝"
  },
  {
    number: 4,
    title: "Rate & Review",
    description: "Share your experience to help other buyers. Your feedback helps maintain our quality standards and builds trust.",
    icon: "⭐"
  },
];

const benefits = [
  {
    icon: Clock,
    title: "Under 1 Minute",
    description: "Quick and easy matching process",
  },
  {
    icon: Gift,
    title: "Completely Free",
    description: "No charges for using our platform",
  },
  {
    icon: Handshake,
    title: "No Obligation",
    description: "Connect without commitment",
  },
  {
    icon: Shield,
    title: "Verified Experts",
    description: "All professionals are vetted",
  },
];

export function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            How HBN Works
          </h2>
          <p className="text-xl text-gray-600">
            Simple steps to find your perfect home buying team
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 4, 
                  repeat: Infinity, 
                  delay: index * 0.8,
                  ease: "easeInOut"
                }}
                className="w-24 h-24 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6"
              >
                <span className="text-4xl">{step.icon}</span>
              </motion.div>
              <div className="w-8 h-8 bg-purple rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-sm font-bold text-white">{step.number}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                {step.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Additional Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="bg-hbn-blue-light rounded-2xl p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <IconComponent className="text-hbn-blue h-12 w-12 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-800 mb-2">{benefit.title}</h4>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
